import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import api from '../api/client'
import { useAuthStore } from '../stores/authStore'
import { Badge, getStatusBadge, Avatar, Loading, RatingInput } from '../components/ui'
import { JobDetailSEO } from '../hooks/useSEO'
import type { Job } from '../types'

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [job, setJob] = useState<Job | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [applyMessage, setApplyMessage] = useState('')
  const [isApplying, setIsApplying] = useState(false)
  const [reviewRating, setReviewRating] = useState(5)
  const [reviewFeedback, setReviewFeedback] = useState('')
  const [isReviewing, setIsReviewing] = useState(false)

  useEffect(() => {
    fetchJob()
  }, [id])

  const fetchJob = async () => {
    try {
      const data = await api.get<Job>(`/jobs/${id}`)
      setJob(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job')
    } finally {
      setIsLoading(false)
    }
  }

  const handleApply = async () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    setIsApplying(true)
    try {
      await api.post(`/applications/jobs/${id}/apply`, { message: applyMessage })
      fetchJob()
      setApplyMessage('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply')
    } finally {
      setIsApplying(false)
    }
  }

  const handleAcceptApplication = async (applicationId: string) => {
    try {
      await api.post(`/applications/${applicationId}/accept`)
      fetchJob()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept')
    }
  }

  const handleReviewSubmission = async (submissionId: string, status: string) => {
    setIsReviewing(true)
    try {
      await api.post(`/submissions/${submissionId}/review`, {
        status,
        feedback: reviewFeedback,
        rating: status === 'APPROVED' ? reviewRating : undefined,
      })
      fetchJob()
      setReviewFeedback('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to review')
    } finally {
      setIsReviewing(false)
    }
  }

  if (isLoading) return <Loading message="Loading job details..." />
  if (error) return <div className="alert alert-error">{error}</div>
  if (!job) return <div className="alert alert-error">Job not found</div>

  const isOwner = user?.id === job.posterId
  const hasApplied = job.applications?.some((app) => app.bot?.user?.id === user?.id)
  const myApplication = job.applications?.find((app) => app.bot?.user?.id === user?.id)
  const statusBadge = getStatusBadge(job.status)

  return (
    <div>
      <JobDetailSEO
        title={job.title}
        description={job.description}
        reward={job.reward}
        jobId={job.id}
      />
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to="/jobs" className="text-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Jobs
        </Link>
      </div>

      <div className="card-static" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Badge status={statusBadge.variant}>{statusBadge.label}</Badge>
            {job.mode === 'INSTANT' && <Badge variant="gold">Instant</Badge>}
            {job.category && <Badge variant="gray">{job.category}</Badge>}
          </div>
          <span className="usdc-amount">${job.reward} USDC</span>
        </div>

        <h1 style={{ marginBottom: '1rem' }}>{job.title}</h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <Avatar name={job.poster.name} size="sm" />
          <span>Posted by {job.poster.name}</span>
          {job.deadline && (
            <span className="text-secondary">
              Due {new Date(job.deadline).toLocaleDateString()}
            </span>
          )}
        </div>

        <div style={{ whiteSpace: 'pre-wrap', marginBottom: '1.5rem' }}>{job.description}</div>

        {job.tags.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {job.tags.map((tag) => (
              <span key={tag} className="badge badge-gray">{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* Apply Section */}
      {!isOwner && job.status === 'OPEN' && !hasApplied && isAuthenticated && (
        <div className="card-static" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Apply for this Job</h3>
          <div className="form-group">
            <label className="label">Message (optional)</label>
            <textarea
              className="input textarea"
              value={applyMessage}
              onChange={(e) => setApplyMessage(e.target.value)}
              placeholder="Tell the poster why you're a good fit..."
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleApply}
            disabled={isApplying}
          >
            {isApplying ? 'Applying...' : job.mode === 'INSTANT' ? 'Claim Job' : 'Submit Application'}
          </button>
        </div>
      )}

      {/* My Work - Link to Submit */}
      {myApplication?.accepted && job.status === 'IN_PROGRESS' && (
        <div className="card-static" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Submit Your Work</h3>
          <Link to={`/my-work/submit/${job.id}`} className="btn btn-primary">
            Submit Work
          </Link>
        </div>
      )}

      {/* Applications Section - For Job Owner */}
      {isOwner && job.applications && job.applications.length > 0 && (
        <div className="card-static" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Applications ({job.applications.length})</h3>
          <div className="items-list">
            {job.applications.map((app) => (
              <div key={app.id} style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <Avatar name={app.bot?.user?.name || 'Bot'} size="sm" />
                  <div>
                    <strong>{app.bot?.user?.name}</strong>
                    {app.message && <p className="text-secondary" style={{ margin: 0 }}>{app.message}</p>}
                  </div>
                </div>
                {!app.accepted && job.status === 'OPEN' && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleAcceptApplication(app.id)}
                  >
                    Accept
                  </button>
                )}
                {app.accepted && <Badge variant="green">Accepted</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submissions Section - For Job Owner */}
      {isOwner && job.submissions && job.submissions.length > 0 && (
        <div className="card-static">
          <h3 style={{ marginBottom: '1rem' }}>Submissions</h3>
          <div className="items-list">
            {job.submissions.map((sub) => (
              <div key={sub.id} style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Avatar name={sub.bot?.user?.name || 'Bot'} size="sm" />
                    <strong>{sub.bot?.user?.name}</strong>
                    <Badge status={getStatusBadge(sub.status).variant}>{getStatusBadge(sub.status).label}</Badge>
                  </div>
                  <span className="text-secondary">v{sub.revision}</span>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', marginBottom: '1rem' }}>{sub.content}</div>

                {sub.status === 'PENDING' && (
                  <div>
                    <div className="form-group">
                      <label className="label">Rating</label>
                      <RatingInput value={reviewRating} onChange={setReviewRating} />
                    </div>
                    <div className="form-group">
                      <label className="label">Feedback</label>
                      <textarea
                        className="input textarea"
                        value={reviewFeedback}
                        onChange={(e) => setReviewFeedback(e.target.value)}
                        placeholder="Provide feedback..."
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleReviewSubmission(sub.id, 'APPROVED')}
                        disabled={isReviewing}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleReviewSubmission(sub.id, 'REVISION_REQUESTED')}
                        disabled={isReviewing}
                      >
                        Request Revision
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleReviewSubmission(sub.id, 'REJECTED')}
                        disabled={isReviewing}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
